import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { chatbotApi } from './api'
import type { ChatbotRequest } from './types'

export function useSendChat() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (payload: ChatbotRequest) => chatbotApi.send(payload),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
      if (res.thread_id) {
        qc.invalidateQueries({
          queryKey: ['tenant', tenantId, 'chat', 'thread', res.thread_id, 'messages'],
        })
      }
    },
  })
}

export function useChatThreads() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: ['tenant', tenantId, 'chat', 'threads'],
    queryFn: () => chatbotApi.listThreads(),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useChatThreadMessages(threadId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: ['tenant', tenantId, 'chat', 'thread', threadId, 'messages'],
    queryFn: () => chatbotApi.getThreadMessages(threadId as string),
    enabled: !!threadId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateChatThread() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (payload: import('./api').ChatCreateThreadPayload = {}) =>
      chatbotApi.createThread(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
    },
  })
}

export function useRenameChatThread() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      chatbotApi.renameThread(id, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
    },
  })
}

export function useSetChatThreadVisibility() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({
      id,
      visibility,
      sharedUserIds,
      sharedRoleIds,
    }: {
      id: string
      visibility: import('./types').ChatThreadVisibility
      sharedUserIds?: number[]
      sharedRoleIds?: number[]
    }) =>
      chatbotApi.setThreadVisibility(id, {
        visibility,
        ...(sharedUserIds ? { shared_user_ids: sharedUserIds } : {}),
        ...(sharedRoleIds ? { shared_role_ids: sharedRoleIds } : {}),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
      qc.invalidateQueries({
        queryKey: ['tenant', tenantId, 'chat', 'thread', vars.id, 'visibility'],
      })
    },
  })
}

export function useThreadVisibility(threadId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: ['tenant', tenantId, 'chat', 'thread', threadId, 'visibility'],
    queryFn: () => chatbotApi.getThreadVisibility(threadId as string),
    enabled: !!threadId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useDeleteChatThread() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: string) => chatbotApi.deleteThread(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
    },
  })
}

export function useDeleteAllChatThreads() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: () => chatbotApi.deleteAllThreads(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'chat', 'threads'] })
    },
  })
}

export function useChatMessageFeedback() {
  return useMutation({
    mutationFn: ({ messageId, rating, comment }: { messageId: string; rating: 1 | -1 | 0; comment?: string }) =>
      chatbotApi.sendFeedback(messageId, { rating, comment }),
  })
}

export function useThreadFeedback(threadId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: ['tenant', tenantId, 'chat', 'thread', threadId, 'feedback'],
    queryFn: () => chatbotApi.listThreadFeedback(threadId as string),
    enabled: !!threadId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useThreadShares(threadId: string | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: ['tenant', tenantId, 'chat', 'thread', threadId, 'shares'],
    queryFn: () => chatbotApi.listShares(threadId as string),
    enabled: !!threadId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useCreateShare() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ threadId, expiresAt }: { threadId: string; expiresAt?: string }) =>
      chatbotApi.createShare(threadId, expiresAt ? { expires_at: expiresAt } : {}),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['tenant', tenantId, 'chat', 'thread', vars.threadId, 'shares'],
      })
    },
  })
}

export function useRevokeShare() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ shareId }: { shareId: string; threadId: string }) =>
      chatbotApi.revokeShare(shareId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ['tenant', tenantId, 'chat', 'thread', vars.threadId, 'shares'],
      })
    },
  })
}
